<script setup lang="ts">
import { useRouter } from "vue-router";

import { ApplyCompletedStages, useApplicationStore, ApplicationStatus } from "@/stores/application";
import { pollApplication, submitPackOnline, sendSubmitEmail, sendPostPackEmail } from "@/data/apply";
import Loader from "@/components/Loader.vue";
import { PHONES } from "@/constants/phones";
import { watch, reactive, onMounted } from "vue";

const store = useApplicationStore();
const router = useRouter();

const handleSendToPage = async (page: string) => {
    if (page === ApplyCompletedStages.SendDirectToLnG && store.applicationId) {
        store.applicationStatus = ApplicationStatus.StartingSubmission;
        store.applicationStatus = await submitPackOnline(store.quoteNumber, store.applicationId);
        if (store.applicationStatus === ApplicationStatus.ErrorSubmission) {
            return;
        }
        await sendSubmitEmail(store.quoteNumber);
    } else if (page === ApplyCompletedStages.SendDirectToDownloadAndPrint) {
        await sendSubmitEmail(store.quoteNumber);
    } else if (page === ApplyCompletedStages.SendDirectToPostPack) {
        await sendPostPackEmail(store.quoteNumber);
    }
    router.push({
        name: page,
        query: {
            quoteNumber: store.quoteNumber,
            premiumId: store.premiumId,
        },
    });
};

const requestPdfPack = async () => {
    if (store.applicationId) {
        try {
            const status = await pollApplication(store.quoteNumber, store.applicationId);
            store.applicationStatus = status;
        } catch (err) {
            store.applicationStatus = ApplicationStatus.ErrorGeneratingPack;
        }
    } else {
        store.applicationStatus = ApplicationStatus.ErrorGeneratingPack;
    }
};

onMounted(() => {
    requestPdfPack();
    getPackGenerationStatusText();
});

const state = reactive({
    generatingPackMessage: "",
});

function getPackGenerationStatusText() {
    if (store.applicationStatus === ApplicationStatus.GeneratingPack) {
        state.generatingPackMessage = "Please wait and do not refresh the page. We are processing your application.";
        setTimeout(() => {
            state.generatingPackMessage = "We're still processing your application. Thank you for your patience.";
        }, 20000);
    } else if (store.applicationStatus === ApplicationStatus.StartingSubmission) {
        state.generatingPackMessage = `Please wait while we send your pack to ${store.companyName}.`;
    }
}

watch(() => store.applicationStatus, getPackGenerationStatusText);

</script>
<template>
    <div class="row chalet mb-3 mt-4 px-3 justify-content-between">
        <div class="col-lg-7 intro-right">
            <div
                v-if="store.applicationStatus === null ||
                    store.applicationStatus === ApplicationStatus.GeneratingPack ||
                    store.applicationStatus === ApplicationStatus.StartingSubmission"
                class="documents-prepare"
            >
                <div class="loader-wrapper mb-4">
                    <Loader
                        v-show="true"
                        :inline="true"
                    />
                </div>
                <strong>
                    {{ state.generatingPackMessage }}
                </strong>
            </div>
            <template
                v-else-if="
                    store.applicationStatus === ApplicationStatus.PackReady ||
                        store.applicationStatus === ApplicationStatus.SuccessfulSubmission"
            >
                <div class="col-12 pb-4 d-flex flex-column">
                    <h3 class="text-secondary blue text-xl-start text-center">
                        Review and submit your application
                    </h3>
                    <p class="pt-1 text-xl-start text-center">
                        Please now:
                    </p>
                </div>
                <ul class="ps-0">
                    <li class="col-12 d-flex flex-column flex-xl-row align-items-center px-0 mt-4">
                        <p class="circle-2 mb-1 mb-xl-0 me-2">
                            1
                        </p>
                        <a
                            id="get_pdf"
                            class="line-height-intro py-0 dark-grey text-underline text-xl-start text-center"
                            :href="`/documents/${store.quoteNumber}/application/${store.applicationId}/pdf/application-form`"
                            target="_blank"
                        >
                            Review your application
                        </a>
                    </li>
                    <li class="col-12 d-flex flex-column flex-xl-row align-items-center px-0 mt-5">
                        <p class="circle-2 mb-1 mb-xl-0 me-2">
                            2
                        </p>
                        <a
                            id="get_medical_pdf"
                            class="line-height-intro py-0 dark-grey text-underline text-xl-start text-center"
                            :href="`/documents/${store.quoteNumber}/application/${store.applicationId}/pdf/medical`"
                            target="_blank"
                        >Review your annuity health form</a>
                    </li>
                    <li class="col-12 d-flex flex-column flex-xl-row align-items-center px-0 mt-5">
                        <p class="pt-1 text-xl-start text-center">
                            And make sure all your details are correct
                            <br>
                            If not, please click the BACK button above and update your information
                        </p>
                    </li>
                    <li class="col-12 d-flex flex-column px-0 px-0 mt-5">
                        <template v-if="store.companySlug === 'lg'">
                            <div class="d-flex flex-column flex-xl-row align-items-center">
                                <p class="circle-2 mb-1 mb-xl-0 me-2">
                                    3
                                </p>
                                <p class="line-height-intro py-0 dark-grey text-underline text-xl-start text-center">
                                    Send your application to Legal & General
                                </p>
                            </div>
                            <button
                                id="apply_online"
                                class="btn btn-forward mt-2 action-btn"
                                type="button"
                                @click="handleSendToPage(ApplyCompletedStages.SendDirectToLnG)"
                            >
                                Click here to send
                            </button>
                        </template>
                        <template v-else>
                            <div class="d-flex flex-column flex-xl-row align-items-center">
                                <p class="circle-2 mb-1 mb-xl-0 me-2">
                                    3
                                </p>
                                <p class="line-height-intro dark-grey ml-0 ml-xl-2 pb-0">
                                    Choose how to receive your application to sign
                                </p>
                            </div>
                            <div class="col-12 d-md-flex flex-column px-0 mt-3">
                                <p class="line-height-intro dark-grey col-12 mt-2 text-xl-start text-center">
                                    Either:
                                </p>
                                <div class="col-12 d-md-flex">
                                    <div class="col-md-5">
                                        <p class="header-lower dark-grey align-self-start mt-md-0 mt-4">
                                            Download and print your application pack *
                                        </p>
                                        <button
                                            id="apply_print"
                                            class="btn btn-forward mt-2 action-btn w-100"
                                            type="button"
                                            @click="handleSendToPage(ApplyCompletedStages.SendDirectToDownloadAndPrint)"
                                        >
                                            Click here
                                        </button>
                                    </div>
                                    <div class="col-md-2 d-md-flex justify-content-center align-items-end">
                                        <p class="py-3 font-weight-bold ">
                                            Or
                                        </p>
                                    </div>
                                    <div class="col-md-5">
                                        <p class="header-lower dark-grey align-self-start mt-md-0 mt-sm-4">
                                            Have your application pack posted to you
                                        </p>
                                        <button
                                            id="apply_post"
                                            class="btn btn-forward mt-2 action-btn w-100"
                                            type="button"
                                            @click="handleSendToPage(ApplyCompletedStages.SendDirectToPostPack)"
                                        >
                                            Click here
                                        </button>
                                    </div>
                                </div>
                                <div class="col-12">
                                    <p class="pt-4 text-center">
                                        (*You only need to print and return your application form and annuity health form)
                                    </p>
                                </div>
                            </div>
                        </template>
                    </li>
                </ul>
            </template>
            <template
                v-else-if="store.applicationStatus === ApplicationStatus.ErrorGeneratingPack ||
                    store.applicationStatus === ApplicationStatus.ErrorSubmission"
            >
                <div class="col-12 pb-3 d-flex flex-column">
                    <h3 class="text-secondary blue text-xl-start text-center">
                        There was a problem {{ store.applicationStatus === ApplicationStatus.ErrorSubmission ? `sending your pack to ${store.companyName}` : 'generating your application pack' }}.
                    </h3>
                    <p
                        v-if="store.applicationStatus === ApplicationStatus.ErrorSubmission"
                        class="pt-1 text-xl-start text-center"
                    >
                        To try again please click retry.
                    </p>
                </div>
                <button
                    v-if="store.applicationStatus === ApplicationStatus.ErrorSubmission"
                    id="retry-application"
                    type="button"
                    class="btn btn-secondary col-4 mb-3"
                    @click="handleSendToPage(ApplyCompletedStages.SendDirectToLnG)"
                >
                    Retry
                </button>
                <p class="pt-1 text-xl-start text-center">
                    An application pack can be printed and posted to you instead.
                </p>
                <p class="pt-1 text-xl-start text-center">
                    Please contact our customer service team on <a href="tel:08003265479">0800 326 5479</a> to request your application pack.
                </p>
                <p class="pt-1 text-xl-start text-center mb-3">
                    When you receive it please:
                </p>
                <ul class="ps-0">
                    <li class="col-12 d-flex flex-column flex-xl-row align-items-center px-0 mt-2">
                        <p class="circle-2 mb-1 mb-xl-0 me-2">
                            1
                        </p>
                        <span>Review your application</span>
                    </li>
                    <li class="col-12 d-flex flex-column flex-xl-row align-items-center px-0 mt-3">
                        <p class="circle-2 mb-1 mb-xl-0 me-2">
                            2
                        </p>
                        <span>Review your annuity health form</span>
                    </li>
                    <li class="col-12 d-flex flex-column flex-xl-row align-items-center px-0 mt-3">
                        <p class="mb-1 mb-xl-0 me-2">
                            And make sure your details are correct and the annuity you have selected is right for you.
                            <br><br>
                            You will then need to return your signed application and annuity health form to us.
                            <br><br>
                            If you have any questions during this process, please call us on
                            <a :href="`tel:${PHONES.ANNUITY_CUSTOMER_CALL_CENTER}`">
                                {{ PHONES.ANNUITY_CUSTOMER_CALL_CENTER }}
                            </a>.
                        </p>
                    </li>
                </ul>
            </template>
        </div>
        <div class="col-lg-4 float d-none d-lg-block ml-auto">
            <img
                class="img-fluid mt-5"
                src="@/assets/images/apply-final.svg"
                alt="Apply final"
            >
        </div>
        <div class="col-12 float d-block d-md-none order-2 mt-4 order-2 mt-5">
            <img
                class="w-75 middle mb-4"
                src="@/assets/images/apply-final.svg"
                alt="Apply final"
            >
        </div>
        <div class="col-12 float d-none d-md-block d-lg-none order-2 mt-5">
            <img
                class="w-50 middle mb-4"
                src="@/assets/images/apply-final.svg"
                alt="Apply final"
            >
        </div>
    </div>
</template>
<style>
    .action-btn {
        background-color: var(--red-ribbon) !important;
        border-color: var(--red-ribbon) !important;
    }

    .documents-prepare {
        display: flex;
        margin: 0 auto;
        max-width: 300px;
        flex-direction: column;
        height: 100%;
        justify-content: center;
        align-items: center;
    }

    .documents-prepare strong {
        text-align: center;
    }

    .loader-wrapper {
        height: 80px;
        width: 80px;
    }
</style>
